import {yupResolver} from '@hookform/resolvers/yup';
import {SaveOutlined} from '@mui/icons-material';
import {Box, Button, Grid, Tab, Tabs, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import QRCode from 'react-qr-code';
import authSelectors from 'src/modules/auth/authSelectors';
import config from 'src/config';
import {i18n} from 'src/i18n';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import {defaultOwnFolioPrefix, normalizeFolioPart, ownFolioPrefixSegment} from 'src/modules/settings/customSequenceModel';
import ubicacionEnumerators from 'src/modules/ubicacion/ubicacionEnumerators';
import {customButtonStyle, magicFlexBox} from 'src/theme/schemes/CustomTheme';
import DivisionAutocompleteFormItem from 'src/view/division/autocomplete/DivisionAutocompleteFormItem';
import GestorDocumentalAutocompleteFormItem from 'src/view/gestorDocumental/autocomplete/GestorDocumentalAutocompleteFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import {
  applyDivisionIdPolicyToPayload,
  canShowDivisionIdFieldInForm,
  isTenantFixedAdminRole,
  mapRecordOrCurrentDivisionToFormDivisionId,
} from 'src/view/shared/division/divisionIdFormPolicy';
import FormWrapper, {FormButtons} from 'src/view/shared/styles/FormWrapper';
import UbicacionAutocompleteFormItem from 'src/view/ubicacion/autocomplete/UbicacionAutocompleteFormItem';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';
import * as yup from 'yup';

const schema = yup.object().shape({
  presupuesto: yupFormSchemas.relationToOne(i18n('entities.activo.fields.presupuesto'), {}),
  foto: yupFormSchemas.images(i18n('entities.ubicacion.fields.foto'), {
    max: 3,
    required: false,
  }),
  nombre: yupFormSchemas.string(i18n('entities.ubicacion.fields.nombre'), {
    required: true,
  }),
  folioPrefix: yupFormSchemas.string(i18n('entities.ubicacion.fields.folioPrefix'), {
    required: false,
  }),
  clasificacion: yupFormSchemas.enumerator(i18n('entities.ubicacion.fields.clasificacion'), {
    required: true,
    options: ubicacionEnumerators.clasificacion,
  }),
  ubicacionAsociada: yupFormSchemas.relationToOne(i18n('entities.ubicacion.fields.ubicacionAsociada'), {}),
  centroTrabajo: yupFormSchemas.relationToOne(i18n('entities.ubicacion.fields.centroTrabajo'), {}),
  ubicacionGoogle: yupFormSchemas.string(i18n('entities.ubicacion.fields.ubicacionGoogle'), {
    required: false,
  }),
  latitud: yupFormSchemas.decimal(i18n('entities.ubicacion.fields.latitud'), {
    required: false,
  }),
  longitud: yupFormSchemas.decimal(i18n('entities.ubicacion.fields.longitud'), {
    required: false,
  }),
  responsables: yupFormSchemas.relationToMany(i18n('entities.ubicacion.fields.responsables'), {
    required: false,
  }),
  jefeArea: yupFormSchemas.relationToOne(i18n('entities.ubicacion.fields.jefeArea'), {
    required: false,
  }),
  capacidad: yupFormSchemas.decimal(i18n('entities.ubicacion.fields.capacidad'), {}),
  documentos: yupFormSchemas.files(i18n('entities.ubicacion.fields.documentos'), {}),
  estado: yupFormSchemas.enumerator(i18n('entities.ubicacion.fields.estado'), {
    options: ubicacionEnumerators.estado,
  }),
  correoContacto: yupFormSchemas.string(i18n('entities.ubicacion.fields.correoContacto'), {}),
  telefonoContacto: yupFormSchemas.integer(i18n('entities.ubicacion.fields.telefonoContacto'), {}),
  propiedad: yupFormSchemas.boolean(i18n('entities.ubicacion.fields.propiedad'), {}),
  empresaExterna: yupFormSchemas.relationToOne(i18n('entities.ubicacion.fields.empresaExterna'), {}),
  visibilidad: yupFormSchemas.enumerator(i18n('entities.ubicacion.fields.visibilidad'), {
    options: ubicacionEnumerators.visibilidad,
  }),
  documentosAdjuntos: yupFormSchemas.relationToMany(i18n('entities.perfilEmpleado.fields.documentosAdjuntos'), {}),
  parentLabel: yupFormSchemas.string(i18n('entities.ubicacion.fields.parentLabel'), {}),
  label: yupFormSchemas.string(i18n('entities.ubicacion.fields.label'), {}),
  divisionId: yupFormSchemas.relationToOne(i18n('entities.division.label'), {required: false}),
});

function TabPanel(props) {
  const {children, value, index, simpleView, ...other} = props;

  return (
    <Box
      sx={
        {
          ...magicFlexBox,
          display: value !== index ? 'none' : 'flex',
        } as any
      }
      role="tabpanel"
      style={{width: '100%', marginTop: simpleView ? -30 : undefined}}
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && (
        <Box
          sx={
            {
              ...magicFlexBox,
              overflow: 'auto',
            } as any
          }
          p={0}
          pt={2}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function UbicacionForm(props) {
  const minimalForm = props.minimalForm || false;
  const tenantAccessContext = useSelector(authSelectors.selectTenantAccessContext);
  const roles = useSelector(authSelectors.selectRoles);
  const isSuperadmin = roles?.includes('admin') ?? false;
  const [tabValue, setTabValue] = useState(0);
  const [initialValues] = useState(() => {
    const record = props.record || {};

    return {
      presupuesto: record.presupuesto,
      foto: record.foto || [],
      nombre: record.nombre,
      folioPrefix: ownFolioPrefixSegment(record.folioPrefix),
      clasificacion: record.clasificacion || 'Area',
      ubicacionAsociada: record.ubicacionAsociada,
      centroTrabajo: record.centroTrabajo,
      ubicacionGoogle: record.ubicacionGoogle,
      latitud: record.latitud,
      longitud: record.longitud,
      responsables: record.responsables,
      jefeArea: record.jefeArea,
      capacidad: record.capacidad,
      documentos: record.documentos || [],
      documentosAdjuntos: record.documentosAdjuntos || [],
      estado: record.estado,
      correoContacto: record.correoContacto,
      telefonoContacto: record.telefonoContacto,
      propiedad: record.propiedad,
      empresaExterna: record.empresaExterna,
      visibilidad: record.visibilidad || 'Visible',
      parentLabel: record.parentLabel,
      label: record.label,
      divisionId: mapRecordOrCurrentDivisionToFormDivisionId(record),
    };
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues as any,
  });

  const showDivisionIdField = canShowDivisionIdFieldInForm(tenantAccessContext, isTenantFixedAdminRole(roles));

  const onSubmit = (values) => {
    const payload = {...values};
    payload.folioPrefix = normalizeFolioPart(ownFolioPrefixSegment(payload.folioPrefix)) || null;
    applyDivisionIdPolicyToPayload(payload, tenantAccessContext, {
      treatAsTransversalSubmit: isTenantFixedAdminRole(roles),
    });
    props.onSubmit(props.record?.id, payload);
  };

  const onReset = () => {
    Object.keys(initialValues).forEach((key) => {
      form.setValue(key, initialValues[key]);
    });
  };

  const nombre = form.watch('nombre');
  const parentLabel = form.watch('parentLabel');
  const folioPrefixPlaceholder = defaultOwnFolioPrefix(nombre);

  useEffect(() => {
    form.setValue('folioPrefix', ownFolioPrefixSegment(props.record?.folioPrefix), {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [props.record?.id, props.record?.folioPrefix, form]);

  useEffect(() => {
    // nombre changed
    let newLabel = nombre;
    if (parentLabel && !nombre.startsWith(parentLabel)) {
      // if nombre does not start with parentLabel
      newLabel = parentLabel + '\\ ' + nombre;
    }
    form.setValue('label', newLabel);
    console.log('newLabel', newLabel);
  }, [nombre, parentLabel]);

  function a11yProps(index) {
    return {
      id: `ubicacion-tab-${index}`,
      'aria-controls': `ubicacion-tabpanel-${index}`,
    };
  }

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const {saveLoading, modal} = props;

  return (
    <FormWrapper>
      <FormProvider {...form}>
        <Grid>
          <Typography variant="h1" sx={{mb: props.record?.id ? 2 : 0}}>
            {nombre}
          </Typography>

          {!minimalForm && props.record?.id && (
            <div style={{marginBottom: 15}}>
              <QRCode
                value={`${config.frontendUrl.protocol}://${config.frontendUrl.host}/ubicacion/${props.record?.id}`}
                size={140}
              />
            </div>
          )}
        </Grid>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          style={
            {
              ...magicFlexBox,
              paddingBottom: 18,
              paddingRight: 18,
              overflow: 'auto',
            } as any
          }>
          <Box
            sx={
              {
                ...magicFlexBox,
                flexGrow: 1,
                bgcolor: 'background.paper',
              } as any
            }>
            <Tabs
              orientation="horizontal"
              variant="scrollable"
              value={tabValue}
              onChange={handleChange}
              aria-label="ubicacion-wizard"
              textColor="secondary"
              indicatorColor="secondary"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
              }}>
              <Tab label="General" {...a11yProps(0)} />
              <Tab label="Adjuntos" {...a11yProps(1)} />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Grid spacing={2} container>
                <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                  <InputFormItem
                    name="nombre"
                    disabled={props.disabled}
                    label={i18n('entities.ubicacion.fields.nombre')}
                    required={true}
                  />
                </Grid>
                <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                  <InputFormItem
                    name="folioPrefix"
                    disabled={props.disabled}
                    label={i18n('entities.ubicacion.fields.folioPrefix')}
                    placeholder={folioPrefixPlaceholder || undefined}
                    defaultValue={ownFolioPrefixSegment(props.record?.folioPrefix)}
                    required={false}
                  />
                </Grid>
                <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                  <SelectFormItem
                    name="clasificacion"
                    disabled={props.disabled}
                    label={i18n('entities.ubicacion.fields.clasificacion')}
                    options={ubicacionEnumerators.clasificacion.map((value) => ({
                      value,
                      label: i18n(`entities.ubicacion.enumerators.clasificacion.${value}`),
                    }))}
                    required={true}
                    menuPortalTarget={document.body}
                  />
                </Grid>

                {showDivisionIdField && !props.disabled && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <DivisionAutocompleteFormItem
                      name="divisionId"
                      label={i18n('entities.division.label')}
                      required={false}
                      showCreate={false}
                      menuPortalTarget={document.body}
                    />
                  </Grid>
                )}

                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <PresupuestoAutocompleteFormItem
                      name="presupuesto"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.presupuesto')}
                      required={false}
                      showCreate={!props.modal}
                    />
                  </Grid>
                )} */}

                {['Area', 'Lugar'].includes(form.watch('clasificacion')) && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <UbicacionAutocompleteFormItem
                      name="ubicacionAsociada"
                      disabled={props.disabled}
                      label={
                        i18n('entities.ubicacion.fields.ubicacionAsociada') +
                        ' a ' +
                        i18n(`entities.ubicacion.enumerators.clasificacion.${form.watch('clasificacion')}`)
                      }
                      onChange={(newUbicacionAsociada) => {
                        //console.log('value changed ubicacionAsociada', newUbicacionAsociada);
                        form.setValue('parentLabel', newUbicacionAsociada?.label);
                      }}
                      required={false}
                      showCreate={!props.modal}
                      menuPortalTarget={document.body}
                    />
                  </Grid>
                )}

                {isSuperadmin && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <UbicacionAutocompleteFormItem
                      name="centroTrabajo"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.centroTrabajo')}
                      required={false}
                      showCreate={!props.modal}
                      menuPortalTarget={document.body}
                    />
                  </Grid>
                )}

                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <GoogleAutocompleteFormItem
                      name="ubicacionGoogle"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.ubicacionGoogle')}
                      required={false}
                      defaultCenter={{
                        lat: -36.8269882,
                        lng: -73.0497665,
                      }}
                      center={{
                        lat: form.watch('latitud'),
                        lng: form.watch('longitud'),
                      }}
                      zoom={14}
                      enableMap={true}
                    />
                  </Grid>
                )} */}

                {!minimalForm && props.isEditing && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <UserAutocompleteFormItem
                      name="responsables"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.responsables')}
                      required={false}
                      mode="multiple"
                      showCreate={!props.modal}
                    />
                  </Grid>
                )}
                {!minimalForm && props.isEditing && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <UserAutocompleteFormItem
                      name="jefeArea"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.jefeArea')}
                      required={false}
                      showCreate={!props.modal}
                    />
                  </Grid>
                )}
                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <InputFormItem
                      name="capacidad"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.capacidad')}
                      required={false}
                    />
                  </Grid>
                )} */}
                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <ImagesFormItem
                      name="foto"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.foto')}
                      required={false}
                      storage={Storage.values.ubicacionFoto}
                      max={3}
                    />
                  </Grid>
                )} */}
                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <FilesFormItem
                      name="documentos"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.documentos')}
                      required={false}
                      storage={Storage.values.ubicacionDocumentos}
                      max={undefined}
                      formats={undefined}
                    />
                  </Grid>
                )} */}
                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <SelectFormItem
                      name="estado"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.estado')}
                      options={ubicacionEnumerators.estado.map((value) => ({
                        value,
                        label: i18n(`entities.ubicacion.enumerators.estado.${value}`),
                      }))}
                      required={false}
                    />
                  </Grid>
                )} */}
                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <InputFormItem
                      name="correoContacto"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.correoContacto')}
                      required={false}
                    />
                  </Grid>
                )}
                {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <InputNumberFormItem
                      name="telefonoContacto"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.telefonoContacto')}
                      required={false}
                    />
                  </Grid>
                )} */}
                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <SwitchFormItem
                      name="propiedad"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.propiedad')}
                    />
                  </Grid>
                )}

                {form.watch('propiedad') && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <EmpresaAutocompleteFormItem
                      name="empresaExterna"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.empresaExterna')}
                      required={false}
                      showCreate={!props.modal}
                    />
                  </Grid>
                )} */}

                {/* {!minimalForm && (
                  <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                    <SelectFormItem
                      name="visibilidad"
                      disabled={props.disabled}
                      label={i18n('entities.ubicacion.fields.visibilidad')}
                      options={ubicacionEnumerators.visibilidad.map((value) => ({
                        value,
                        label: i18n(`entities.ubicacion.enumerators.visibilidad.${value}`),
                      }))}
                      required={false}
                    />
                  </Grid>
                )} */}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid item lg={props.drawer ? 12 : 10} md={8} sm={12} xs={12}>
                <GestorDocumentalAutocompleteFormItem
                  name="documentosAdjuntos"
                  label={i18n('entities.perfilEmpleado.fields.documentosAdjuntos')}
                  disabled={props.disabled}
                  required={false}
                  showCreate={!props.modal}
                  defaultprops={{tipo: 'Documento'}}
                  showButton={true}
                  mode="multiple"
                  disableAutocomplete
                />
              </Grid>
            </TabPanel>
          </Box>
        </form>
        {!props.disabled && (
          <FormButtons
            style={{
              flexDirection: modal ? 'row-reverse' : undefined,
            }}>
            <Button
              style={{
                width: props.drawer ? '100%' : 300,
                ...customButtonStyle,
                marginTop: '12px',
              }}
              variant="contained"
              color="secondary"
              disabled={saveLoading}
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              startIcon={<SaveOutlined />}
              size="large">
              {i18n('common.save')}
            </Button>

            {/* <Button
              disabled={saveLoading}
              onClick={onReset}
              type="button"
              startIcon={<UndoIcon />}
              size="small"
            >
              {i18n('common.reset')}
            </Button>

            {props.onCancel ? (
              <Button
                disabled={saveLoading}
                onClick={() => props.onCancel()}
                type="button"
                startIcon={<CloseIcon />}
                size="small"
              >
                {i18n('common.cancel')}
              </Button>
            ) : null} */}
          </FormButtons>
        )}
      </FormProvider>
    </FormWrapper>
  );
}

export default UbicacionForm;
